import * as THREE from 'three';
import { Font } from 'three/addons/loaders/FontLoader.js';
import * as BufferGeometryUtils from 'three/addons/utils/BufferGeometryUtils.js';
import { LineSegments2 } from 'three/addons/lines/LineSegments2.js';
import { LineGeometry } from 'three/addons/lines/LineGeometry.js';
import { LineMaterial } from 'three/addons/lines/LineMaterial.js';
import { sceneUnitsFromMm } from './scaler';

const LINE_SPACING_FACTOR = 0.6; // Halves the font's default line spacing for multi-line text

// Shared invisible material for the text hit box: fully transparent, but visible=true so it still
// participates in raycasting (see hitBoxMesh.visible below and isFullyVisible() in sceneObjectUtils.ts).
const hitBoxMaterial = new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false });
hitBoxMaterial.name = "HitBoxMaterial";

const HIT_LINE_WIDTH_PX = 12; // Generous invisible click margin (screen-space px), independent of the visible style width

let hitLineMaterial: LineMaterial | undefined;
const getHitLineMaterial = (): LineMaterial => {
    if (!hitLineMaterial) {
        hitLineMaterial = new LineMaterial({
            linewidth: HIT_LINE_WIDTH_PX,
            resolution: new THREE.Vector2(window.innerWidth, window.innerHeight),
            transparent: true,
            opacity: 0,
            depthTest: false,
            depthWrite: false
        });
        hitLineMaterial.name = "AnnotationHitLine";
    }
    return hitLineMaterial;
};

// Invisible, wider backing line placed alongside a visible annotation line to make it easier to click.
// Pass the visible line's own LineGeometry: if the caller only ever mutates it via setPositions()
// (never reassigns .geometry), the hit line stays in sync automatically with no extra update code.
export const createHitLine = (geometry: LineGeometry): LineSegments2 => {
    const hitLine = new LineSegments2(geometry, getHitLineMaterial());
    hitLine.name = "HitLine";
    return hitLine;
};

const computeHitBoxSize = (geometry: THREE.BufferGeometry, size: number): THREE.Vector3 => {
    geometry.computeBoundingBox();
    const bb = geometry.boundingBox;
    const padding = size * 0.5;
    const width = (bb ? bb.max.x - bb.min.x : size) + padding * 2;
    const height = (bb ? bb.max.y - bb.min.y : size) + padding * 2;
    const depth = Math.max(size * 0.3, sceneUnitsFromMm(15));
    return new THREE.Vector3(width, height, depth);
};

const getTextShapeGeometry = (text: string, font: Font, size: number ): THREE.ShapeGeometry => {
        const lines = text.split('\n');

        if (lines.length === 1) {
            const shape = font.generateShapes(text, size);
            return new THREE.ShapeGeometry(shape, 2);
        }

        const { boundingBox, underlineThickness, resolution } = font.data;
        const scale = size / resolution;
        const lineHeight = (boundingBox.yMax - boundingBox.yMin + underlineThickness) * scale * LINE_SPACING_FACTOR;

        const lineGeometries = lines.map((line, i) => {
            const shape = font.generateShapes(line, size);
            const geometry = new THREE.ShapeGeometry(shape, 2);
            geometry.translate(0, -i * lineHeight, 0);
            return geometry;
        });

        return BufferGeometryUtils.mergeGeometries(lineGeometries) as THREE.ShapeGeometry;
    }

export const updateDoubleSidedText = (  newText: string, font: Font, size: number,
                                        textGroup: THREE.Group, textMaterial: THREE.Material ) => {
        
    const geometry = getTextShapeGeometry(newText, font, size); // Get the text geometry
    geometry.center();
    if (textGroup.children.length == 0) {
        const frontTextMesh = new THREE.Mesh(geometry, textMaterial); // Create a new text mesh with the new geometry
        frontTextMesh.name = "FrontTextMesh";
        const backTextMesh = frontTextMesh.clone();
        backTextMesh.name = "BackTextMesh";
        const hitBoxSize = computeHitBoxSize(geometry, size);
        const hitBoxGeometry = new THREE.BoxGeometry(hitBoxSize.x, hitBoxSize.y, hitBoxSize.z);
        const hitBoxMesh = new THREE.Mesh(hitBoxGeometry, hitBoxMaterial); // Fully transparent, but stays raycastable
        hitBoxMesh.name = "HitBoxMesh";

        frontTextMesh.quaternion.copy(textGroup.quaternion); // Apply rotation to the text mesh
        backTextMesh.quaternion.copy(textGroup.quaternion); // Apply rotation to the text mesh
        hitBoxMesh.quaternion.copy(textGroup.quaternion);

        backTextMesh.rotateY(Math.PI); // Clone the text mesh for the back side

        textGroup.add(frontTextMesh); // Add the text mesh to the group
        textGroup.add(backTextMesh); // Add the text mesh to the group
        textGroup.add(hitBoxMesh); 

        /* Helper plane to visualize the text group
        const cubeGeometry = new THREE.BoxGeometry(1, 0.5, 0.001); // Adjust size as needed
        const cubeMaterial = new THREE.MeshStandardMaterial({ color: 0x00ff00 }); // Or any material
        const cubeMesh = new THREE.Mesh(cubeGeometry, cubeMaterial);
        cubeMesh.quaternion.copy(this.textGroup.quaternion); // Optional: align with group rotation
        this.textGroup.add(cubeMesh); // Add the cube mesh to the group */

        /* Helper to visualizer rotation
        const axesHelper = new THREE.AxesHelper(1); // Size = 1 unit
        this.textGroup.add(axesHelper); */

    }
    else {
        // Identify by name, not by geometry class: multi-line text's merged geometry (via
        // BufferGeometryUtils.mergeGeometries) is a plain THREE.BufferGeometry at runtime despite
        // the `as THREE.ShapeGeometry` cast in getTextShapeGeometry, so `instanceof THREE.ShapeGeometry`
        // silently never matches for multi-line text, leaving the mesh's geometry stuck on its very
        // first build forever.
        const hitBoxSize = computeHitBoxSize(geometry, size);
        textGroup.traverse(child => {
            if (child instanceof THREE.Mesh && (child.name === "FrontTextMesh" || child.name === "BackTextMesh")) {
                child.geometry.dispose();
                child.geometry = geometry;
                child.material = textMaterial;
            }
            else if (child instanceof THREE.Mesh && child.name === "HitBoxMesh") {
                child.geometry.dispose();
                child.geometry = new THREE.BoxGeometry(hitBoxSize.x, hitBoxSize.y, hitBoxSize.z);
            }
        });
    }
}

export const getUpDirectionForAnnotationByPlaneNormal = (normal: THREE.Vector3, modelRotationOffset: number) => {
    
    const zAxis = new THREE.Vector3(0, 0, 1);
    const dot = zAxis.dot(normal);

    if (Math.abs(dot) < 0.5) 
        return zAxis;
    else {
        const upDirection = new THREE.Vector3(0, 1, 0);
        upDirection.applyAxisAngle(zAxis, modelRotationOffset);
        return upDirection;
    }
        
}
